import { mapValues } from "lodash-es";
import { computed, reactive } from "vue";

import type { Action, ActionData } from "@/action/types";
import type { ClickableEmits, ClickableProps } from "@/composables/Props";
import type { ActionSource } from "@/model/action";
import { useGlobalI18n } from "@/translations/i18n";
import { injectStrict } from "@/utils/vue";

import { actionSourceKey } from "./injectKeys";

const { t } = useGlobalI18n();

// style must NOT be used for component props
type ActionAttrs = ClickableProps & { style?: Record<"display", "none"> } & ClickableEmits;

interface ActionOptions {
  click?: () => void;
  // inject only resolves during setup and render, so computed getters must pass the source
  source?: ActionSource;
}

export function action<TParams extends unknown[], TReturn>(
  action: Action<TParams, TReturn>,
  options?: ActionOptions,
  ...params: TParams
): ActionAttrs {
  const name = action.data.name;
  const text = name ? t(name) : "";
  const label = action.data.label;
  const actionSource = options?.source ?? injectStrict(actionSourceKey);
  const attrs = {
    icon: action.data.icon,
    text,
    shortcut: action.data.shortcut.name(),
    ariaLabel: label ? t(label) : text,
    onClick: () => {
      action(actionSource, ...params);
      options?.click?.();
    },
  };
  return action.data.active || action.data.disabled ? reactiveAttrs(attrs, action.data) : attrs;
}

function reactiveAttrs(attrs: ActionAttrs, data: ActionData<never[], unknown>) {
  // calling reactive unwraps the computed values so the types are correct
  if (data.active) {
    attrs.activated = computed(() => data.active?.()) as never;
  }
  if (data.disabled) {
    attrs.style = computed(() => (data.disabled?.() ? { display: "none" } : undefined)) as never;
  }
  return reactive(attrs);
}

export const actionMap = actionMapWith<[]>();

// filters an ActionMap by the given parameter types,
// applies the parameter values and converts them to a map of ActionAttrs
export function actionMapWith<TParams extends unknown[]>(...params: TParams) {
  return function <T extends ActionMap<T>>(
    actions: T | undefined,
    options?: ActionOptions,
  ): ActionAttrsFor<T, TParams> {
    return mapValues(actions, (a) =>
      a ? action(a as Action<TParams, unknown>, options, ...params) : undefined,
    ) as never;
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ActionMap<T> = { [K in keyof T]: Action<any[], unknown> | undefined };

// return the keys of the actions that match the given param types
type KeysHavingParams<T extends ActionMap<T>, TParams extends unknown[]> = {
  [K in keyof T]: NonNullable<T[K]> extends Action<infer P, unknown>
    ? TParams extends P
      ? K
      : never
    : never;
}[keyof T];

type ActionAttrsFor<T extends ActionMap<T>, TParams extends unknown[]> = Record<
  KeysHavingParams<T, TParams>,
  ActionAttrs
>;
